import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnimatePresence, motion } from 'framer-motion';

import { RootState } from '../../store/store';
import { hideNotificationModal } from '../../store/slices/notification/notificationModalSlice';
import { useNotification } from '../../hooks/useNotification';
import { NOTIFICATION_LEVEL_COLORS } from '../../api/constants/notifications/notificationLevelColors';
import { NOTIFICATION_LEVEL_ICONS } from '../../api/constants/notifications/notificationLevelIcons';

import { useScrollLock } from '../../hooks/useScrollLock';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { Info, X } from 'lucide-react';

/**
 * 🔔 Modal especializado para notificaciones
 *
 * NO usa FormModal porque:
 * - no es formulario
 * - tiene comportamiento propio
 * - diseño específico para alertas
 */
const NotificationModal: React.FC = () => {
  const dispatch = useDispatch();

  const { handleMarkAsRead } = useNotification();

  const { isOpen, notification } = useSelector(
    (state: RootState) => state.notificationModal
  );

  useScrollLock(isOpen);

  useEscapeKey(() => {
    if (isOpen) {
      dispatch(hideNotificationModal());
    }
  });

  if (!notification) return null;

  // =========================================================
  // 🔒 Cerrar modal
  // =========================================================

  const handleClose = () => {
    dispatch(hideNotificationModal());
  };

  // =========================================================
  // ✅ Confirmar lectura
  // =========================================================

  const handleConfirmRead = async () => {
    await handleMarkAsRead(notification.id);
    dispatch(hideNotificationModal());
  };

  // =========================================================
  // 🎨 Resolver color principal
  // =========================================================

  const color =
    NOTIFICATION_LEVEL_COLORS[notification.level?.toUpperCase()] || '#3BA2B8';

  // =========================================================
  // 🧩 Resolver icono
  // =========================================================

  const level = notification.level?.toUpperCase() || 'INFO';

  const Icon = NOTIFICATION_LEVEL_ICONS[level] || Info;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/70 px-4 py-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="relative w-full max-w-md rounded-xl border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark"
            initial={{
              scale: 0.92,
              opacity: 0,
              y: 20,
            }}
            animate={{
              scale: 1,
              opacity: 1,
              y: 0,
            }}
            exit={{
              scale: 0.92,
              opacity: 0,
              y: 20,
            }}
            transition={{
              duration: 0.25,
              ease: 'easeOut',
            }}
          >
            {/* ❌ Botón cerrar */}
            <button
              onClick={handleClose}
              className="absolute right-4 top-4 rounded-full p-1.5 text-body transition-all duration-200 hover:text-black dark:text-bodydark2 dark:hover:text-white"
              aria-label="Cerrar modal"
            >
              <X size={20} />
            </button>

            {/* ✴️ Icono principal */}
            <div className="pt-8">
              <div className="flex justify-center">
                <Icon className="h-10 w-10" style={{ color }} />
              </div>
            </div>

            {/* 🧭 Contenido */}
            <div className="px-6 py-6 text-center">
              <h2 className="text-title-xsm font-semibold text-black dark:text-white">
                {notification.title}
              </h2>

              {notification.message && (
                <p className="mt-4 whitespace-pre-line break-words text-sm leading-relaxed text-body dark:text-bodydark">
                  {notification.message}
                </p>
              )}

              {notification.createdAt && (
                <p className="mt-4 text-xs italic text-bodydark2">
                  {new Date(notification.createdAt).toLocaleString()}
                </p>
              )}
            </div>

            {/* 🔘 Acciones */}
            <div className="flex justify-center gap-3 border-t border-stroke px-6 py-5 dark:border-strokedark">
              {/* ✅ Acción principal */}
              <button
                onClick={handleConfirmRead}
                className="rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-default transition-all duration-200 hover:opacity-90"
              >
                Marcar como leída
              </button>

              {/* ❌ Acción secundaria */}
              <button
                onClick={handleClose}
                className="rounded-md border border-stroke bg-white px-5 py-2.5 text-sm font-medium text-black shadow-sm transition-all duration-200 hover:bg-gray-2 dark:border-strokedark dark:bg-boxdark dark:text-white dark:hover:bg-meta-4"
              >
                Cerrar
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NotificationModal;
